import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { Spinner, seqStatusColor } from "../components/ui-bits";
import { ActionMenu, ConfirmDialog } from "../components/crud";
import { SequenceForm } from "../components/record-forms";
import { useToast } from "../hooks/use-toast";
import { Send, Plus, Search, Play, Pause, Mail } from "lucide-react";

function Metric({ label, value }) {
  return (
    <div className="text-center px-3">
      <div className="text-[15px] font-semibold tabular leading-none">{value}</div>
      <div className="text-[11px] text-[hsl(240,4%,55%)] mt-1">{label}</div>
    </div>
  );
}

export default function SequencesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = () => api.sequences().then(setList).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const toggle = async (s, e) => {
    e.stopPropagation();
    const next = s.status !== "Active";
    const updated = await api.activateSequence(s.id, next);
    setList((prev) => prev.map((x) => (x.id === s.id ? { ...x, status: updated.status } : x)));
    toast({ title: `Sequence ${next ? "activated" : "paused"}`, description: s.name });
  };

  const submit = async (values) => {
    const sequence = modal.mode === "create" ? await api.createSequence(values) : await api.updateSequence(modal.row.id, values);
    setList((prev) => modal.mode === "create" ? [sequence, ...prev] : prev.map((item) => item.id === sequence.id ? { ...item, ...sequence } : item));
    setModal(null);
    toast({ title: `Sequence ${modal.mode === "create" ? "created" : "updated"}`, description: sequence.name });
  };

  const remove = async () => {
    setDeleteBusy(true);
    try {
      await api.deleteSequence(deleting.id);
      setList((prev) => prev.filter((item) => item.id !== deleting.id));
      toast({ title: "Sequence deleted", description: deleting.name });
      setDeleting(null);
    } catch (error) {
      toast({ title: "Could not delete sequence", description: error?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setDeleteBusy(false);
    }
  };

  if (loading) return <Spinner />;
  const filtered = list.filter((s) => s.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="h-full flex flex-col animate-in-up">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[hsl(240,6%,92%)]">
        <div className="flex items-center gap-1.5 rounded-md border border-[hsl(240,6%,90%)] px-2 py-[5px] w-[220px]">
          <Search className="h-[15px] w-[15px] text-[hsl(240,4%,55%)]" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search sequences…" className="w-full bg-transparent text-[13px] outline-none placeholder:text-[hsl(240,4%,60%)]" />
        </div>
        <button onClick={() => setModal({ mode: "create", row: {} })} className="ml-auto flex items-center gap-1.5 rounded-md brand-bg text-white px-2.5 py-[6px] text-[13px] font-medium hover:brightness-105"><Plus className="h-4 w-4" /> New sequence</button>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="max-w-[1100px] mx-auto space-y-3">
          {filtered.length === 0 && <div className="text-center py-16 text-[13px] text-[hsl(240,4%,55%)]">No sequences yet. Create your first strategy.</div>}
          {filtered.map((s) => (
            <div key={s.id} className="group rounded-xl border border-[hsl(240,6%,91%)] bg-white hover:shadow-sm transition-shadow">
              <div className="flex items-center gap-4 p-4">
                <div className="h-10 w-10 rounded-lg brand-bg-soft flex items-center justify-center shrink-0"><Send className="h-[18px] w-[18px] text-[hsl(245,50%,48%)]" /></div>
                <button onClick={() => navigate(`/sequences/${s.id}`)} className="min-w-0 flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-[14.5px] font-semibold truncate">{s.name}</span>
                    <span className={`inline-flex rounded-md px-1.5 py-0.5 text-[11px] font-medium ${seqStatusColor[s.status]}`}>{s.status}</span>
                    {s.pending_approval > 0 && <span className="inline-flex rounded-md bg-amber-100 text-amber-700 px-1.5 py-0.5 text-[11px] font-medium">{s.pending_approval} to review</span>}
                  </div>
                  <div className="text-[12.5px] text-[hsl(240,4%,50%)] mt-0.5 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{s.goal} · by {s.created_by}</div>
                </button>
                <div className="hidden md:flex items-center divide-x divide-[hsl(240,6%,92%)]">
                  <Metric label="Enrolled" value={s.enrolled} />
                  <Metric label="Active" value={s.active} />
                  <Metric label="Replied" value={s.replied} />
                  <Metric label="Sent" value={s.sent_steps} />
                </div>
                <div className="flex items-center gap-1 shrink-0 pl-2">
                  <button onClick={(e) => toggle(s, e)} title={s.status === "Active" ? "Pause" : "Activate"} className="p-1.5 rounded-md text-[hsl(240,4%,45%)] hover:bg-[hsl(240,5%,95%)]">{s.status === "Active" ? <Pause className="h-[17px] w-[17px]" /> : <Play className="h-[17px] w-[17px]" />}</button>
                  <ActionMenu onEdit={() => setModal({ mode: "edit", row: s })} onDelete={() => setDeleting(s)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <SequenceForm open={!!modal} onClose={() => setModal(null)} onSubmit={submit} initialValues={modal?.row} />
      <ConfirmDialog open={!!deleting} onClose={() => !deleteBusy && setDeleting(null)} onConfirm={remove} busy={deleteBusy} title="Delete sequence?" description={`“${deleting?.name || "This sequence"}” and its enrollments will be permanently removed.`} />
    </div>
  );
}
